/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useState, useContext } from 'react';
import UseAxios from '../../Api/http/http';
import { Typography, Tab, Tabs, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SetIcon from '@mui/icons-material/Add';
import {
  SET_TARGET,
  ADD_EDIT_DIALOG,
  EDIT_TARGET,
  TARGET_DASHBOARD,
} from '../../Constants/strings';
import CircularProgressBar from '../../Components/CircularProgressbar/circularProgressbar';
import ButtonComponent from '../../Components/Button/button';
import './targetDashboard.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import TargetDashboardTable from './targetDashboardTable';
import { getTargets } from '../../Api/request/target';
import { verticalData } from '../../Api/request/master'; // Assuming you have a file that exports the API call function
import SimpleSnackbar from '../../Components/Snackbar/snackbar';
import { generateNextYears, years } from './SetTarget/setTargetUtils';
import { AuthContext } from '../../Contexts/AuthContext';
import NoTargetData from './noTarget';
import { MODULE_NAMES } from '../../Constants/modules';
import PuzzleLoader from '../../Assets/images/svg/loading puzzle.gif';
// Initial state for the quarters of the year
const initialQuarters = Array.from({ length: 4 }, (quarter, index) => ({
  quarter: index + 1,
  targets: [],
}));

const Targets = () => {
  const navigate = useNavigate(); // Use the navigate hook
  const location = useLocation(); // Hook to get location
  const [targetDetails, setTargetDetails] = useState([]);
  const [totalSetTarget, setTotalSetTarget] = useState(0); // State for total set target
  const [totalPending, setTotalPending] = useState(0); // State for total pending target
  const [verticals, setVerticals] = useState([]);
  const [totalQuarterSetTargets, setTotalQuarterSetTargets] = useState({}); // State for total quarter set targets
  const [totalQuarterPendingTargets, setTotalQuarterPendingTargets] = useState(
    {},
  ); // State for total quarter pending targets
  const [statusCode, setStatusCode] = useState(null); // State for statusCode
  const [selectedVertical, setSelectedVertical] = useState([]);
  const [allTargets, setAllTargets] = useState({});
  const [snackbar, setSnackbar] = useState({
    message: '',
    severity: '',
    open: false,
  });
  const [targetSetYears, setTargetSetYears] = useState([]); // State for targetSetYears
  const [addYearDropdown, setAddYearDropdown] = useState([]); // State for addYearDropdown
  const [sortedVerticals, setSortedVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userProfile } = useContext(AuthContext);
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.CERTIFICATION_TARGET] || []
    : [];
  const hasAccess = (action) => {
    return permission.includes(action);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch verticals data from the API
        const response = await UseAxios(verticalData());
        const result = response.data.verticalDetails.map((verts) => ({
          id: verts.id,
          name: verts.verticalName,
        }));
        setVerticals(result);
        const mapQuarters = (quarter) => ({
          ...quarter,
        });

        setAllTargets(
          result.reduce((accumulator, vertical) => {
            accumulator[vertical.name] = initialQuarters.map(mapQuarters);
            return accumulator;
          }, {}),
        );
      } catch (error) {
        snackbarOpen(`${TARGET_DASHBOARD.OOPS_TRY_AGAIN}`, 'warning');
        console.error('verticals error', error);
      }
    };
    fetchData();
  }, []);

  const fetchTargets = async () => {
    setLoading(true);
    try {
      // get target details from the API
      const getCurrentYear = () => new Date().getFullYear();
      const response = await UseAxios(getTargets(getCurrentYear()));
      const result = response.data;
      if (result.response && result.response?.data?.statusCode === 500) {
        snackbarOpen(`${TARGET_DASHBOARD.SERVER_DOWN}`, 'warning');
        return;
      }
      if (result.response && result.response.data.statusCode === 441) {
        setStatusCode(441); // Set the statusCode to 441 when no data
        setAddYearDropdown(years);
      } else {
        setTargetDetails(result.targetDetails);
        setTotalSetTarget(result.totalTargetsCount[0].totalSetTarget); // Set the total set target
        setTotalPending(result.totalTargetsCount[0].totalPending); // Set the total pending target
        setTotalQuarterSetTargets(result.totalSetTargets[0]); // Set the total quarter set targets
        setTotalQuarterPendingTargets(result.totalPendingTargets[0]); // Set the total quarter pending targets
        setTargetSetYears(result.targetSetYears); // Set the targetSetYears state
      }
    } catch (error) {
      snackbarOpen(`${TARGET_DASHBOARD.OOPS_TRY_AGAIN}`, 'warning');
      console.error('targets error', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const countTargetsForVertical = (verticalName) => {
      if (!targetDetails || !verticals) return [];
      const vertical = verticals.find((verts) => verts.name === verticalName);
      if (!vertical) return 0;

      return targetDetails.reduce((total, target) => {
        if (target.verticalId === vertical.id) {
          return total + target.currentQuarterTarget;
        }
        return total;
      }, 0);
    };

    const sorted = [...verticals].sort((a, b) => {
      const countA = countTargetsForVertical(a.name);
      const countB = countTargetsForVertical(b.name);
      return countB - countA;
    });

    setSortedVerticals(sorted);
    setSelectedVertical(sorted[0]?.name || '');
  }, [verticals, targetDetails]);

  useEffect(() => {
    fetchTargets();
  }, [location]);

  useEffect(() => {
    const addDropdown = generateNextYears(targetSetYears);
    setAddYearDropdown(addDropdown);
  }, [targetSetYears]);

  // Displays the snackbar with a message and severity
  const snackbarOpen = (message, severity) => {
    setSnackbar({ message, severity, open: true });
    setTimeout(() => setSnackbar((prev) => ({ ...prev, open: false })), 4000);
  };
  // Handles changing the selected vertical via tabs
  const handleTabChange = (event, newValue) => {
    setSelectedVertical(newValue);
  };

  const filterTargetsByVertical = (verticalName) => {
    if (!targetDetails || !verticals) return [];
    const vertical = verticals.find((verts) => verts.name === verticalName);
    return vertical
      ? targetDetails.filter((target) => target.verticalId === vertical.id)
      : [];
  };

  const targetPercentage = totalSetTarget
    ? ((totalSetTarget - totalPending) / totalSetTarget) * 100
    : 0;

  // Check if current year and next 3 years are in targetSetYears
  const isAddTargetDisabled = () => {
    const currentYear = new Date().getFullYear()
      .toString();
    const next3Years = Array.from({ length: 4 }, (year, index) =>
      (parseInt(currentYear) + index).toString(),
    );
    return next3Years.every((year) => targetSetYears.includes(year));
  };

  return (
    <div className="target-dashboard-parent">
      <div className="target-dashboard">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <img
              src={PuzzleLoader}
              alt="Loading..."
              className="loader-gif"
              style={{ width: '100px', height: 'auto' }}
            />
          </div>
        ) : (
          <div className="dashboard-target-container">
            <div className="dashboard-target-title">
              <Typography className="dashboard-target-title-text">
                {SET_TARGET.TARGETS}
              </Typography>
            </div>
            <div className="dashboard-target-button">
              {statusCode !== 441 ? (
                <div className="dashboard-edit-target">
                  <ButtonComponent
                    label={ADD_EDIT_DIALOG.EDIT_TARGET}
                    variant="contained"
                    startIcon={<EditIcon />}
                    btnCss="edit-target-btn"
                    onClick={() => {
                      localStorage.setItem(
                        'targetSetYears',
                        JSON.stringify(targetSetYears),
                      );
                      navigate('/certifications/targets/edit-target');
                    }}
                    id="edit-target-btn"
                    disabled={!hasAccess('update')}
                  ></ButtonComponent>
                </div>
              ) : null}
              <div className="dashboard-set-target">
                {isAddTargetDisabled() ? (
                  <Tooltip classes={{ tooltip: 'set-target-btn-tooltip' }} title={TARGET_DASHBOARD.TOOLTIP_TEXT}>
                    <Tooltip>
                      <div>
                        <ButtonComponent
                          label={ADD_EDIT_DIALOG.SET_TARGET}
                          variant="contained"
                          startIcon={<SetIcon />}
                          btnCss="add-target-btn"
                          id="add-target-btn"
                          disabled
                        />
                      </div>
                    </Tooltip>
                  </Tooltip>
                ) : (
                  <ButtonComponent
                    label={ADD_EDIT_DIALOG.SET_TARGET}
                    variant="contained"
                    startIcon={<SetIcon />}
                    btnCss="add-target-btn"
                    onClick={() => {
                      localStorage.setItem(
                        'addYearDropdown',
                        JSON.stringify(addYearDropdown),
                      );
                      navigate('/certifications/targets/set-target');
                    }}
                    id="add-target-btn"
                    disabled={!hasAccess('create')}
                  ></ButtonComponent>
                )}
              </div>
            </div>
            {statusCode === 441 ? (
              <NoTargetData message={TARGET_DASHBOARD.NO_TARGETS} />
            ) : (
              <>
                <div className="dashboard-quarter-summary">
                  <div className="dashboard-quarter-summary-container">
                    <div className="dashboard-total-target">
                      <div className="dashboard-quarter-card">
                        <Typography className="dashboard-target-quarter-title">
                          {SET_TARGET.TOTAL_TARGET}
                        </Typography>
                        <div className="dashboard-target-calculation">
                          <CircularProgressBar
                            progressBarCss="dashboard-progress-bar"
                            value={targetPercentage}
                          />
                          <Typography className="dashboard-total-target-count">
                            {totalSetTarget}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    {allTargets[selectedVertical]?.map((quarter) => {
                      const quarterPercentage = totalQuarterSetTargets[
                        `quarter${quarter.quarter}`
                      ]
                        ? ((totalQuarterSetTargets[`quarter${quarter.quarter}`]
                          - totalQuarterPendingTargets[
                            `quarter${quarter.quarter}`
                          ])
                          / totalQuarterSetTargets[`quarter${quarter.quarter}`])
                        * 100
                        : 0;

                      return (
                        <div
                          className="dashboard-quarter-card-container"
                          key={quarter.quarter}
                        >
                          <div className="dashboard-quarter-card">
                            <Typography className="dashboard-target-quarter-title">
                              {EDIT_TARGET.TARGET_QUARTER} {quarter.quarter}
                            </Typography>
                            <div className="dashboard-target-calculation">
                              <CircularProgressBar value={quarterPercentage} />
                              <Typography className="dashboard-quarter-count">
                                {totalQuarterSetTargets[
                                  `quarter${quarter.quarter}`
                                ] || 0}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="dashboard-target-tabs">
                  <Tabs
                    value={selectedVertical}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    id="target-vertical-tabs"
                  >
                    {sortedVerticals.map((vertical) => (
                      <Tab
                        key={vertical.name}
                        label={vertical.name}
                        value={vertical.name}
                      /> // Maps each vertical to a tab
                    ))}
                  </Tabs>
                </div>
                <div className="dashboard-target-table">
                  {initialQuarters.map((quarter) => {
                    const quarterTargets = filterTargetsByVertical(
                      selectedVertical,
                    ).filter((target) => target.quarter === quarter.quarter);
                    if (quarterTargets.length > 0) {
                      return (
                        <TargetDashboardTable
                          key={quarter.quarter}
                          quarterTargets={quarterTargets}
                          quarter={quarter.quarter}
                        />
                      );
                    } else {
                      return null;
                    }
                  })}
                </div>
              </>
            )}
          </div>
        )}
        <SimpleSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          alertCss={`target-dashboard-snackbar ${snackbar.severity}`}
        />
      </div>
    </div>
  );
};

export default Targets;
